/* Changelog-Pflege für Souvera-Apps (Superadmin-only).
 * Öffentlicher Endpunkt: GET /api/v1/changelogs/{app_id}
 * Admin-Endpunkte: /api/admin/infra/changelogs
 */
import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Save, X, FileText, ChevronDown, ChevronRight, Loader2 } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const APPS = [
    { id: "souvera_mail", label: "Souvera Mail" },
    { id: "souvera_central", label: "Souvera Central" },
    { id: "souvera_shield", label: "Souvera Shield" },
    { id: "souvera_mailarchiv", label: "Souvera Mailarchiv" },
];

function ChangelogRow({ entry, onEdit, onDelete, busy }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-zinc-800 rounded-sm">
            <div className="flex items-center justify-between px-3 py-1.5">
                <button
                    onClick={() => setOpen(!open)}
                    className="flex items-center gap-2 text-xs font-mono text-zinc-300 hover:text-zinc-100"
                >
                    {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                    <span className="text-emerald-400">v{entry.version}</span>
                    <span className="text-zinc-600">{entry.date}</span>
                    <span className="text-zinc-400">{entry.title}</span>
                </button>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onEdit(entry)}
                        className="px-2 py-0.5 text-[10px] font-mono text-zinc-500 hover:text-zinc-300 border border-zinc-700 hover:border-zinc-500 rounded-sm"
                    >Edit</button>
                    <button
                        onClick={() => onDelete(entry.id)}
                        disabled={busy === entry.id}
                        className="px-2 py-0.5 text-[10px] font-mono text-red-400 hover:text-red-300 border border-red-900/30 hover:border-red-700/50 rounded-sm disabled:opacity-30"
                    >
                        {busy === entry.id ? <Loader2 size={9} className="animate-spin" /> : <Trash2 size={9} />}
                    </button>
                </div>
            </div>
            {open && entry.body && (
                <div className="px-3 py-2 border-t border-zinc-800 text-[11px] text-zinc-400 font-mono whitespace-pre-wrap leading-relaxed">
                    {entry.body}
                </div>
            )}
        </div>
    );
}

function ChangelogForm({ entry, apps, onSave, onCancel, busy }) {
    const [appId, setAppId] = useState(entry?.app_id || apps[0]?.id || "souvera_mail");
    const [version, setVersion] = useState(entry?.version || "");
    const [date, setDate] = useState(entry?.date || new Date().toISOString().slice(0, 10));
    const [title, setTitle] = useState(entry?.title || "");
    const [body, setBody] = useState(entry?.body || "");

    const handleSave = () => {
        if (!appId || !version || !date || !title) return;
        onSave({ app_id: appId, version, date, title, body });
    };

    return (
        <div className="border border-zinc-700 bg-zinc-900/50 rounded-sm p-3 space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                    <label className="text-[10px] text-zinc-500 font-mono uppercase block mb-0.5">App</label>
                    <select
                        value={appId}
                        onChange={(e) => setAppId(e.target.value)}
                        className="w-full px-2 py-1 text-[11px] font-mono bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-sm"
                        disabled={!!entry}
                    >
                        {apps.map((a) => (
                            <option key={a.id} value={a.id}>{a.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] text-zinc-500 font-mono uppercase block mb-0.5">Version</label>
                    <input
                        value={version}
                        onChange={(e) => setVersion(e.target.value)}
                        placeholder="0.22.14"
                        className="w-full px-2 py-1 text-[11px] font-mono bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-sm"
                        disabled={!!entry}
                    />
                </div>
                <div>
                    <label className="text-[10px] text-zinc-500 font-mono uppercase block mb-0.5">Date</label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full px-2 py-1 text-[11px] font-mono bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-sm"
                    />
                </div>
                <div className="flex items-end gap-1">
                    <button
                        onClick={handleSave}
                        disabled={busy || !appId || !version || !date || !title}
                        className="px-3 py-1 text-[11px] font-mono bg-emerald-600 text-white rounded-sm hover:bg-emerald-500 disabled:opacity-40 flex items-center gap-1"
                    >
                        {busy ? <Loader2 size={9} className="animate-spin" /> : <Save size={9} />}
                        Save
                    </button>
                    <button
                        onClick={onCancel}
                        className="px-3 py-1 text-[11px] font-mono text-zinc-400 hover:text-zinc-200 rounded-sm flex items-center gap-1"
                    >
                        <X size={9} /> Cancel
                    </button>
                </div>
            </div>
            <div>
                <label className="text-[10px] text-zinc-500 font-mono uppercase block mb-0.5">Title</label>
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="EXDEV-safe install"
                    maxLength={200}
                    className="w-full px-2 py-1 text-[11px] font-mono bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-sm"
                />
            </div>
            <div>
                <label className="text-[10px] text-zinc-500 font-mono uppercase block mb-0.5">
                    Changelog (Markdown, max 5000 characters)
                </label>
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="- `rename()` → `copyRecursive()` for cross-mount compatibility&#10;- SelfUpdate now also runs on NFS mounts"
                    maxLength={5000}
                    rows={4}
                    className="w-full px-2 py-1 text-[11px] font-mono bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-sm resize-y"
                />
            </div>
        </div>
    );
}

export default function ChangelogAdmin({ appFilter }) {
    const { data, mutate, isLoading } = useSWR("/api/admin/infra/changelogs", fetcher);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);
    const [delBusy, setDelBusy] = useState(null);
    const [err, setErr] = useState(null);

    const entries = (data || []).filter((e) => (appFilter ? e.app_id === appFilter : true));

    const handleSave = async (body) => {
        setBusy(true);
        setErr(null);
        try {
            await api.post("/api/admin/infra/changelogs", body);
            await mutate();
            setShowForm(false);
            setEditing(null);
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    const handleDelete = async (id) => {
        setDelBusy(id);
        setErr(null);
        try {
            await api.delete(`/api/admin/infra/changelogs/${id}`);
            await mutate();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setDelBusy(null);
    };

    if (isLoading) {
        return <div className="text-[11px] text-zinc-500 font-mono px-3 py-6 text-center"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading changelogs…</div>;
    }

    return (
        <div data-testid="changelog-admin" className="space-y-2">
            {err && (
                <div className="px-3 py-2 text-[11px] text-red-400 bg-red-500/5 border border-red-900/30 rounded-sm font-mono">{err}</div>
            )}

            <div className="flex items-center justify-between">
                <span className="text-[11px] text-zinc-500 font-mono">{entries.length} entries</span>
                <button
                    onClick={() => { setShowForm(true); setEditing(null); }}
                    className="px-2 py-0.5 text-[11px] font-mono border border-emerald-700/50 text-emerald-400 hover:bg-emerald-500/10 rounded-sm flex items-center gap-1"
                    data-testid="changelog-add-btn"
                >
                    <Plus size={9} /> New
                </button>
            </div>

            {(showForm || editing) && (
                <ChangelogForm
                    entry={editing}
                    apps={APPS}
                    onSave={handleSave}
                    onCancel={() => { setShowForm(false); setEditing(null); }}
                    busy={busy}
                />
            )}

            {entries.length === 0 && !showForm && !editing && (
                <div className="text-[11px] text-zinc-600 font-mono text-center py-6">
                    No changelogs yet. Click &quot;New&quot; to create one.
                </div>
            )}

            {entries.map((e) => (
                <ChangelogRow
                    key={e.id}
                    entry={e}
                    onEdit={(entry) => { setEditing(entry); setShowForm(false); }}
                    onDelete={handleDelete}
                    busy={delBusy}
                />
            ))}
        </div>
    );
}
